import { scheduleOwner } from './scheduleOwner';
import { getScheduleDurationHours } from './scheduleDuration';
import './DriverDayTimeline.css';
import { assignTimelineLanes } from './timelineLanes';

const TIME_AXIS_HOURS = ['06:00', '09:00', '12:00', '15:00', '18:00', '22:00'];
const TIMELINE_START_HOUR = 6;
const TIMELINE_END_HOUR = 22;
const DISPATCHER_TIMELINE_BUFFER_HOURS = 0;
const TIMELINE_AREA_LABELS = {
  tokyo: '东京',
  narita_airport: '成田',
  haneda_airport: '羽田',
  yokohama: '横滨',
  yokohama_port: '横滨港',
  hakone: '箱根',
  odawara: '小田原',
  mt_fuji: '富士山',
  fuji: '富士山',
  nikko: '日光',
  kawagoe: '川越',
  shizuoka: '静冈',
  osaka: '大阪',
  kyoto: '京都',
  nara: '奈良',
  kobe: '神户',
  kansai_airport: '关西机场',
  itami_airport: '伊丹',
  nagoya: '名古屋',
};

export default function DriverDayTimeline({ day, onSelectTimelineItem }) {
  const activeItems = day.items.filter(item => !['cancelled', 'canceled', '已取消'].includes(item.status));
  const entries = assignTimelineLanes(activeItems.map(item => {
    const visual = getTimelineVisualRange(item);
    return visual && { item, range: { start: visual.startMinutes, end: visual.endMinutes } };
  }).filter(entry => entry && entry.range.end > 360 && entry.range.start < 1320));
  const isFree = activeItems.length === 0;
  const availableText = getAvailableWindowText(activeItems);
  const hasConflict = activeItems.some((_, index) => getTimelineConflictLevel(activeItems, index) !== 'none');

  return (
    <div className="grid gap-3 rounded-lg border border-border-subtle bg-background/40 p-3 lg:grid-cols-[120px_1fr_130px] lg:items-center">
      <div>
        <div className="text-sm font-semibold text-foreground">{day.label}</div>
        <div className="text-xs text-muted-foreground">周{day.weekday}</div>
      </div>
      <div className="min-w-0">
        {hasConflict && (
          <div
            className="mb-2 inline-flex rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-200"
            data-testid="dispatcher-timeline-conflict-warning"
          >
            时间接近，请点开排班块查看详情
          </div>
        )}
        <div className="driver-day-time-axis mb-2 flex items-center justify-between text-[11px] text-muted-foreground">
          {TIME_AXIS_HOURS.map(hour => (
            <span key={hour}>{hour}</span>
          ))}
        </div>
        <div className="overflow-x-auto pb-1">
          <div className="driver-day-track relative rounded-lg border border-border-subtle bg-surface" style={{ height: `${Math.max(1, ...entries.map(entry => entry.lane + 1)) * 72 + 16}px` }}>
            <div className="driver-day-grid absolute inset-y-0 left-0 right-0 grid grid-cols-5">
              {TIME_AXIS_HOURS.slice(0, -1).map(hour => (
                <div key={hour} className="border-r border-border-subtle/70 last:border-r-0" />
              ))}
            </div>
            {isFree ? (
              <div className="absolute inset-1 flex items-center justify-center rounded-md bg-success/15 text-xs font-semibold text-success">
                全天空闲
              </div>
            ) : (
              entries.map(({ item, lane }, index) => (
                <TimelineBookingBlock
                  key={`${item.id || item.order_no || item.time || index}-${index}`}
                  item={item}
                  index={index}
                  lane={lane}
                  conflictLevel={getTimelineConflictLevel(activeItems, activeItems.indexOf(item))}
                  onClick={() => onSelectTimelineItem?.(item)}
                />
              ))
            )}
          </div>
        </div>
        <div className="driver-day-mobile-summaries">
          {entries.map(({item}, index) => <button type="button" key={item.id || index} disabled={item.timeline_type === 'rest'} onClick={() => onSelectTimelineItem?.(item)} style={{color:item.timeline_type === 'rest' ? '#ffb6a5' : scheduleOwner(item).color}}><b>{index + 1}</b><span>{formatTimelineOrderSummary(item)}</span></button>)}
        </div>
        {activeItems.filter(item => {
          const range = getTimelineVisualRange(item);
          return !range || range.endMinutes <= 360 || range.startMinutes >= 1320;
        }).map((item, index) => (
          <button key={item.id || `untimed-${index}`} type="button" onClick={() => onSelectTimelineItem?.(item)} className="mt-2 block w-full rounded-md border border-amber-400/30 bg-amber-500/10 p-2 text-left text-xs text-amber-200">
            {getTimelineVisualRange(item) ? '时间轴外' : '时间待确认'} · {formatTimelineOrderSummary(item)}
          </button>
        ))}
        {day.items.filter(item => ['cancelled', 'canceled', '已取消'].includes(item.status)).map((item, index) => (
          <button key={item.id || `cancelled-${index}`} type="button" onClick={() => onSelectTimelineItem?.(item)} className="mt-2 block w-full rounded-md border border-border-subtle p-2 text-left text-xs text-muted-foreground">
            已取消 · {formatTimelineOrderSummary(item)}
          </button>
        ))}
        <p className="mt-1 text-[11px] text-muted-foreground">点击排班块查看详情</p>
      </div>
      <div className="flex items-center justify-start lg:justify-end">
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isFree ? 'bg-success/15 text-success' : 'bg-amber-500/15 text-amber-200'}`}>
          可派时间 {availableText}
        </span>
      </div>
    </div>
  );
}

function TimelineBookingBlock({ item, index, lane = 0, conflictLevel = 'none', onClick }) {
  const visualRange = getTimelineVisualRange(item);
  if (!visualRange) return null;
  const start = getTimelinePercentFromMinutes(visualRange.startMinutes);
  const end = getTimelinePercentFromMinutes(visualRange.endMinutes);
  const width = Math.max(0, end - start);
  const typeMeta = getTimelineTypeMeta(item);
  const summary = formatTimelineOrderSummary(item);
  const isConflict = conflictLevel !== 'none';
  const owner = item.timeline_type === 'rest' ? null : scheduleOwner(item);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={item.timeline_type === 'rest'}
      aria-label={summary}
      className="driver-day-booking text-left text-xs text-foreground"
      data-lane={lane}
      style={{ top: `${8 + lane * 72}px`, left: `${start}%`, width: `${Math.min(width, 100 - start)}%` }}
      title={owner ? `${summary} · ${owner.label}` : summary}
    >
      <span aria-hidden="true" className={`driver-day-duration-bar rounded border ${typeMeta.className} ${isConflict ? 'ring-2 ring-amber-300' : ''}`}
        style={{
          ...(owner ? { backgroundColor: `color-mix(in srgb, ${owner.color} 25%, #151718)`, borderColor: owner.color } : {}) }} />
      <span className="block" data-testid="dispatcher-timeline-order-summary">
        {isConflict ? '!' : ''}{summary}
      </span>
      <span className="driver-day-mobile-number" aria-hidden="true">{index + 1}</span>
      {item.timeline_type !== 'rest' && (
        <span className="driver-day-full-route">
          {[item.pickup_location || getTimelineLocationLabel(item, 'pickup'), item.dropoff_location || getTimelineLocationLabel(item, 'dropoff')].filter(Boolean).join(' → ') || item.route_summary}
        </span>
      )}
    </button>
  );
}

function getTimelineTypeMeta(item) {
  if (item.timeline_type === 'rest') return { label: '司机休息', className: 'border border-rose-300/50 bg-rose-950 text-rose-100' };
  if (item.timeline_type === 'official') {
    return { label: '正式订单', className: 'border border-[#ffe7a3]/65 bg-[linear-gradient(135deg,#f2d77d,#b68b32)] text-[#141006]' };
  }
  if (item.timeline_type === 'driver_extra') {
    return { label: '司机追加', className: 'border border-[#f3d98b]/45 bg-[#8a6a24] text-[#fff7dc]' };
  }
  if (item.timeline_type === 'dispatcher_extra') {
    return { label: '调度追加', className: 'border border-[#d8b76a]/35 bg-[#5a4316] text-[#fff1bf]' };
  }
  return { label: '排班', className: 'border border-[#d8b76a]/30 bg-[#3a2a10] text-[#f3d98b]' };
}

function getTimelineConflictLevel(items, index) {
  const current = items[index];
  const currentRange = getTimelineVisualRange(current);
  if (!currentRange) return 'none';

  const hasConflict = items.some((item, itemIndex) => {
    if (itemIndex === index) return false;
    const otherRange = getTimelineVisualRange(item);
    if (!otherRange) return false;
    return currentRange.startMinutes < otherRange.endMinutes && otherRange.startMinutes < currentRange.endMinutes;
  });

  return hasConflict ? 'overlap' : 'none';
}

function formatTimelineOrderSummary(item) {
  if (item.timeline_type === 'rest') return item.all_day !== false ? '全天休息' : `${item.start_time}-${item.end_time} 休息`;
  const time = item.time || '--:--';
  const pickup = getTimelineLocationLabel(item, 'pickup');
  const dropoff = getTimelineLocationLabel(item, 'dropoff');
  const route = pickup && dropoff
    ? `${pickup}-${dropoff}`
    : item.route_summary || pickup || dropoff || getTimelineTypeMeta(item).label;

  return `${time} ${route}`;
}

function getTimelineLocationLabel(item, direction) {
  const rawValue = direction === 'pickup'
    ? item.pickup_area || item.pickup_location
    : item.dropoff_area || item.dropoff_location;
  const fallbackValue = direction === 'pickup'
    ? item.pickup_location || item.pickup_area
    : item.dropoff_location || item.dropoff_area;
  const value = rawValue || fallbackValue;
  if (!value) return '';

  const normalizedValue = String(value).trim();
  return TIMELINE_AREA_LABELS[normalizedValue] || compactTimelineLocation(normalizedValue);
}

function compactTimelineLocation(value) {
  const label = TIMELINE_AREA_LABELS[value] || value;
  const firstPart = label
    .split(/[、,，/／|｜]/)[0]
    .split(/\s+/)[0]
    .split('　')[0]
    .trim();
  if (!firstPart) return '';
  return firstPart.length > 8 ? `${firstPart.slice(0, 8)}...` : firstPart;
}

function getTimelinePercentFromMinutes(minutes) {
  if (minutes === null || minutes === undefined) return 0;
  const decimalHour = minutes / 60;
  const clamped = Math.max(TIMELINE_START_HOUR, Math.min(TIMELINE_END_HOUR, decimalHour));
  return ((clamped - TIMELINE_START_HOUR) / (TIMELINE_END_HOUR - TIMELINE_START_HOUR)) * 100;
}

function getTimelineMinutes(time) {
  if (!time) return null;
  const [hourText, minuteText = '0'] = String(time).split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}

function getTimelineDurationHours(item) {
  return getScheduleDurationHours(item);
}

function getTimelineVisualRange(item) {
  if (item?.timeline_type === 'rest') {
    const start = item.all_day !== false ? 360 : getTimelineMinutes(item.start_time);
    const end = item.all_day !== false ? 1320 : getTimelineMinutes(item.end_time);
    return start === null || end === null ? null : { startMinutes: start, endMinutes: end };
  }
  const startMinutes = getTimelineMinutes(item?.time);
  if (startMinutes === null) return null;
  const durationMinutes = getTimelineDurationHours(item) * 60;
  const bufferMinutes = DISPATCHER_TIMELINE_BUFFER_HOURS * 60;
  return {
    startMinutes: startMinutes - bufferMinutes,
    serviceStartMinutes: startMinutes,
    serviceEndMinutes: startMinutes + durationMinutes,
    endMinutes: startMinutes + durationMinutes + bufferMinutes,
  };
}

function getAvailableWindowText(items) {
  if (items.some(item => item.timeline_type === 'rest' && item.all_day !== false)) return '全天休息';
  if (items.some(item => item.timeline_type === 'rest')) return '部分时段休息，需核对';
  if (items.length === 0) return '06:00-22:00';
  if (items.some(item => !getTimelineVisualRange(item))) return '有订单时间待确认';
  const latestHour = items.reduce((latest, item) => {
    const visualRange = getTimelineVisualRange(item);
    if (!visualRange) return latest;
    return Math.max(latest, visualRange.endMinutes / 60);
  }, TIMELINE_START_HOUR);
  if (latestHour >= TIMELINE_END_HOUR) return '已排满';
  const latestMinutes = Math.round(latestHour * 60);
  return `${String(Math.floor(latestMinutes / 60)).padStart(2, '0')}:${String(latestMinutes % 60).padStart(2, '0')}后可派`;
}


